import { useMutation, useQueryClient } from '@tanstack/react-query'

import { clearDefaultRichMenuFromAllChannels } from 'api/richMenu'
import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context'
import { getQueryOrganizationId } from 'configs/queryClient'
import { queryKeys } from 'constants/queryKeys'
import { lineStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { toErrorMessage } from 'hooks/utils'
import type { TRichMenuCurrentResponse } from 'types'
import { toRichMenuOperationConflict } from './richMenuOperationConflict'

export function useClearDefaultRichMenuFromAllChannels() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null
  const mutation = useMutation({
    mutationFn: () => clearDefaultRichMenuFromAllChannels(organizationId),
    // 戻る/進むで組織が変わった後に解決しても、別組織のキャッシュへ書かない
    onMutate: () => ({ queryOrganizationId: getQueryOrganizationId() }),
    onSettled: async (response, error, _variables, context) => {
      if (!user?.uid) return
      if (context?.queryOrganizationId !== getQueryOrganizationId()) return

      const queryKey = [queryKeys.RICH_MENU_CURRENT, user.uid]
      if (!response || response.failed.length > 0) {
        if (!response && toRichMenuOperationConflict(error)) return

        void queryClient.invalidateQueries({ queryKey, exact: true })
        return
      }

      await queryClient.cancelQueries({ queryKey, exact: true })
      queryClient.setQueryData<TRichMenuCurrentResponse>(
        queryKey,
        (current) => {
          const lineChannelId =
            current?.lineChannelId ??
            response.cleared[0]?.lineChannelId ??
            response.alreadyCleared[0]?.lineChannelId

          if (lineChannelId === undefined) return current

          return {
            lineChannelId,
            richMenuId: null,
            richMenu: null,
            imageContentType: null,
            imageBase64: null,
          }
        },
      )
    },
  })

  return {
    clearDefaultFromAllChannels: mutation.mutateAsync,
    isClearing: mutation.isPending,
    error: mutation.error
      ? toErrorMessage(
          mutation.error,
          lineStrings.management.richMenu.create.errors.clearDefaultFailed,
        )
      : undefined,
    resetError: mutation.reset,
  }
}
